import { useState } from "react";
import { Button, TextField } from "@legends/ui";
import { register, resendVerification, ApiError, type AuthUser } from "./api";

export interface SignUpScreenProps {
  /** Return to the sign-in screen (which carries the Google/Apple buttons). */
  onBackToLogin: () => void;
  /** Called if a verified session is somehow established inline (unused today —
   *  verification completes via the emailed link, not here). Kept for symmetry. */
  onLogin?: (user: AuthUser) => void;
}

// Local Fan registration (LSC-554 / ADR 0233): email + password, alongside the
// Google/Apple SSO on the sign-in screen ("enable both"). A new account is created
// UNVERIFIED — we show a "check your email" state and never mint a session here; the
// fan confirms via the emailed link (/verify-email) then signs in. Styled to match
// LoginScreen (the Cave sign-in card).
export default function SignUpScreen({ onBackToLogin }: SignUpScreenProps) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Once registered, swap the form for a "verify your email" confirmation + resend.
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resent, setResent] = useState(false);

  const canSubmit = !busy && email.trim() !== "" && password.length >= 8 && password === confirm;

  const submit = () => {
    if (!canSubmit) {
      if (password.length > 0 && password.length < 8) setError("Password must be at least 8 characters.");
      else if (confirm.length > 0 && password !== confirm) setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    setError(null);
    register(email.trim(), password, displayName.trim() || undefined)
      .then((r) => setSentTo(r.email))
      .catch((err: unknown) =>
        setError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Sign-up failed. Please try again.",
        ),
      )
      .finally(() => setBusy(false));
  };

  const resend = () => {
    if (!sentTo) return;
    setResent(false);
    resendVerification(sentTo)
      .then(() => setResent(true))
      .catch(() => setResent(true)); // always reassure — the endpoint never reveals state
  };

  return (
    <main
      className="mer-canvas mer-canvas--photo"
      style={{ minHeight: "calc(100vh / var(--ui-zoom, 1))", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          padding: "26px 32px 24px",
          width: 360,
          animation: "cnPop var(--dur) var(--ease-out)",
        }}
      >
        {sentTo ? (
          <div style={{ display: "grid", gap: 14 }}>
            <h1 style={{ margin: 0, fontSize: "var(--fs-h2)", letterSpacing: "var(--ls-tight)" }}>Check your email</h1>
            <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-dim)", lineHeight: 1.6 }}>
              We sent a verification link to <strong style={{ color: "var(--text)" }}>{sentTo}</strong>. Click it to
              activate your account, then sign in.
            </p>
            <Button full onClick={resend} disabled={resent}>
              {resent ? "Verification email re-sent" : "Resend verification email"}
            </Button>
            <Button variant="accent" full onClick={onBackToLogin}>
              Back to sign in
            </Button>
          </div>
        ) : (
          <>
            <h1 style={{ margin: "0 0 4px", fontSize: "var(--fs-h2)", letterSpacing: "var(--ls-tight)" }}>Create your account</h1>
            <p style={{ margin: "0 0 4px", fontSize: "var(--fs-sm)", color: "var(--text-dim)" }}>
              Join with email, or use Google / Apple on the sign-in screen.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              style={{ marginTop: 16, display: "grid", gap: 14 }}
            >
              <TextField label="Email" name="email" type="email" value={email} onChange={setEmail} autoFocus full />
              <TextField label="Display name (optional)" name="displayName" value={displayName} onChange={setDisplayName} full />
              <TextField label="Password" name="password" type="password" value={password} onChange={setPassword} full />
              <TextField
                label="Confirm password"
                name="confirmPassword"
                type="password"
                value={confirm}
                onChange={setConfirm}
                onSubmit={submit}
                full
              />
              {error && (
                <p role="alert" style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--alert-2)" }}>
                  {error}
                </p>
              )}
              <Button variant="accent" full disabled={!canSubmit} type="submit">
                {busy ? "Creating account…" : "Create account"}
              </Button>
            </form>
            <button
              type="button"
              onClick={onBackToLogin}
              style={{
                marginTop: 16,
                background: "none",
                border: "none",
                color: "var(--accent)",
                cursor: "pointer",
                fontSize: "var(--fs-sm)",
                width: "100%",
              }}
            >
              Already have an account? Sign in
            </button>
          </>
        )}
      </div>
    </main>
  );
}
